/**
 * Shared customer-map renderer.
 *
 * One Leaflet renderer for the customer-location map (points come from ticket
 * visits), used by both CRM → Customers and Tech Support → Customer map so the
 * two can't drift. Both hit the same `/api/crm/customers/map` endpoint.
 *
 * A per-container Leaflet instance is cached (keyed by the container element),
 * so re-rendering the same tab just refreshes markers.
 */
import { escapeHtml } from '../../src/lib/format.js';
import * as api from '../api-client.js';

const _instances = new WeakMap(); // container element -> { map, cluster }

function statusColor(s) {
  switch (String(s || '').toLowerCase()) {
    case 'active': return '#22c55e';
    case 'suspended': return '#f59e0b';
    case 'blocked': return '#ef4444';
    default: return '#94a3b8';
  }
}

function label(s) {
  return String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Render (or refresh) the customer map into `container`.
 * @param {{container: HTMLElement, metaEl?: HTMLElement}} opts
 */
export async function renderCustomerMap({ container, metaEl }) {
  if (!container) return;
  if (typeof L === 'undefined') {
    if (metaEl) metaEl.textContent = 'Map library still loading — try again in a moment.';
    return;
  }
  if (metaEl) metaEl.textContent = 'Loading…';
  let data;
  try {
    data = await api.fetch(api.routes.crmCustomerMap(), { method: 'GET' });
  } catch (err) {
    if (metaEl) metaEl.textContent = (err && err.message) || 'Failed to load map.';
    return;
  }
  const points = Array.isArray(data && data.items) ? data.items : [];

  let inst = _instances.get(container);
  if (!inst) {
    const map = L.map(container);
    L.tileLayer(data.tile_url || 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: data.tile_attribution || '© OpenStreetMap contributors',
    }).addTo(map);
    const cluster = (L.markerClusterGroup ? L.markerClusterGroup() : L.layerGroup()).addTo(map);
    map.setView([13.45, -16.57], 11); // default view: Banjul area
    inst = { map, cluster };
    _instances.set(container, inst);
  } else {
    inst.cluster.clearLayers();
  }

  const bounds = [];
  points.forEach((p) => {
    if (p.lat == null || p.lng == null) return;
    const color = statusColor(p.status);
    const marker = L.circleMarker([p.lat, p.lng], {
      radius: 7, color, fillColor: color, fillOpacity: 0.85, weight: 1,
    });
    const pkg = p.package ? escapeHtml(p.package) : '—';
    const st = p.status ? escapeHtml(label(p.status)) : 'Unknown';
    marker.bindPopup(
      `<strong>${escapeHtml(p.name || '')}</strong><br>Status: ${st}<br>Package: ${pkg}`
      + (p.address ? `<br>${escapeHtml(p.address)}` : '')
    );
    inst.cluster.addLayer(marker);
    bounds.push([p.lat, p.lng]);
  });
  if (bounds.length) {
    try { inst.map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 }); } catch { /* noop */ }
  }
  // Leaflet needs a size recompute once the container becomes visible.
  setTimeout(() => { try { inst.map.invalidateSize(); } catch { /* noop */ } }, 60);
  if (metaEl) {
    metaEl.textContent = `${points.length} customer${points.length === 1 ? '' : 's'} with a known location`;
  }
}

export function customerMapExportUrl() {
  return api.routes.crmCustomerMapExport();
}
